import { Box, Card, CardContent, Typography } from "@material-ui/core";
import classnames from "classnames";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, useNotify } from "react-admin";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import { CompactFormToolbar } from "../../../../../common/form/CompactFormToolbar";
import { CompactSaveButton } from "../../../../../common/form/CompactSaveButton";
import { useAxiosInstance } from "../../../../../../common/hooks/useAxiosInstance";
import {
  DriverListDto,
  DriverViewApiFactory,
  EventApiFactory,
} from "../../../../../../common/data/service-proxies";
import { handleAxiosError } from "../../../../../../common/data/axiosErrorHandler";
import { ClearAll, Close } from "@material-ui/icons";
import { HookFormDriverPicker } from "../../../../../common/form/hook-form/inputs/HookFormDriverPicker";

interface Props {
  selectedRows: number[];
  currentCoDrivers?: Array<number | null | undefined>;
  onSuccess: () => void;
  onFailed?: () => void;
}

type FormValues = {
  coDriverId: number | null;
};

const DefaultCompactFormToolbar = ({ children }: PropsWithChildren<any>) => {
  const {
    formState: { isSubmitting },
  } = useFormContext();
  return (
    <CompactFormToolbar>
      <CompactSaveButton saving={isSubmitting} disabled={isSubmitting} />
      {children}
    </CompactFormToolbar>
  );
};

export const ChangeSelectedEventsCoDriverForm = ({
  selectedRows,
  currentCoDrivers,
  onSuccess,
  onFailed,
  ...rest
}: Props) => {
  const [data, setData] = useState<DriverListDto[]>();
  const [loaded, setLoaded] = useState<boolean>(false);
  const notify = useNotify();
  const axios = useAxiosInstance();
  const selectedCoDrivers = useMemo(
    () => Array.from(new Set(currentCoDrivers?.filter((x) => x)).values()),
    [currentCoDrivers]
  );

  useEffect(() => {
    if (!axios) {
      return;
    }

    if (!selectedCoDrivers.filter((x) => !!x).length) {
      return;
    }

    DriverViewApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppDriverviewGetManyGet(selectedCoDrivers as number[])
      .then((response) => {
        setData(response.data);
      })
      .finally(() => setLoaded(true));
  }, [axios, selectedCoDrivers]);

  const onSubmit = useCallback(
    (data: FormValues) => {
      if (!axios) {
        return;
      }

      return EventApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppEventBulksetcodriverPost({
          selectedEvents: selectedRows,
          coDriverId: data.coDriverId,
        })
        .then(() => {
          onSuccess();
        })
        .catch((e) => {
          if (onFailed) {
            onFailed();
          }

          console.error(e);
          notify(handleAxiosError(e), { type: "error" });
        });
    },
    [axios, selectedRows, notify, onSuccess, onFailed]
  );

  const formContext = useForm<FormValues>({
    mode: "onChange",
  });

  const { handleSubmit, control, watch, setValue } = formContext;
  const coDriverId = watch("coDriverId");

  const handleSubmitInternal: SubmitHandler<FormValues> = (data) => {
    return Promise.resolve(onSubmit(data)).catch((e: Error) => {
      notify(e.message ?? "An unexpected error has occurred", "error");
    });
  };

  return (
    <CardContent>
      <FormProvider {...formContext}>
        <form
          className={classnames("simple-form", "form-create-edit-drawer")}
          {...sanitizeRestProps(rest)}
          onSubmit={handleSubmit(handleSubmitInternal, (err) =>
            console.error(err)
          )}>
          <Card>
            <CardContent>
              <div className="ra-input">
                <HookFormDriverPicker<FormValues>
                  control={control}
                  name="coDriverId"
                  label="Co-driver"
                  variant="outlined"
                  defaultValue={
                    selectedCoDrivers.length === 1 ? selectedCoDrivers[0] : null
                  }
                />
              </div>
              {loaded ? (
                <div className="ra-input">
                  <Box display="flex" alignItems="center" gridGap={5}>
                    <Typography variant="body2">
                      Current co-driver(s):
                    </Typography>
                    <Typography variant="body2">
                      {data?.length
                        ? data.map((x) => x.driverName).join(", ")
                        : "None"}
                    </Typography>
                  </Box>
                </div>
              ) : null}
            </CardContent>
          </Card>
          <DefaultCompactFormToolbar>
            {!!coDriverId ? (
              <Button
                label="Clear"
                onClick={() => setValue("coDriverId", null)}>
                <ClearAll />
              </Button>
            ) : null}
            <Button label="Leave unchanged" onClick={onFailed}>
              <Close />
            </Button>
          </DefaultCompactFormToolbar>
        </form>
      </FormProvider>
    </CardContent>
  );
};

const sanitizeRestProps = ({
  active,
  dirty,
  dirtyFields,
  dirtyFieldsSinceLastSubmit,
  dirtySinceLastSubmit,
  error,
  errors,
  form,
  hasSubmitErrors,
  hasValidationErrors,
  initialValues,
  modified = null,
  modifiedSinceLastSubmit,
  save = null,
  submitError,
  submitErrors,
  submitFailed,
  submitSucceeded,
  submitting,
  touched = null,
  valid,
  values,
  visited = null,
  __versions = null,
  ...props
}: any) => props;
